import React, { useState, useEffect } from "react";
import IleyaSavingsDataService from "../services/IleyaSavingsService";
import UserDataService from "../services/UserService";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import { formatWithCommas, formatToTwoDecimals } from "../../utils/formatNumber";

const toDateInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const AddIleyaSavings = () => {
  const { owner } = useParams();
  const navigate = useNavigate();
  const initialIleyaState = {
    owner: owner || "",
    date: toDateInput(new Date()),
    amount: "",
    creditOrDebit: "credit",
    description: "",
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null
  };

  const [Ileya, setIleya] = useState(initialIleyaState);
  const [ownerDisplay, setOwnerDisplay] = useState(() => (UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : ""));
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!owner) return;
    if (ownerDisplay) return;
    const cached = UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : "";
    if (cached) {
      setOwnerDisplay(cached);
      return;
    }
    let mounted = true;
    UserDataService.get(owner)
      .then(res => {
        const u = res?.data ?? res ?? {};
        const fname = u.firstName || u.firstname || u.first_name || u.name || "";
        const lname = u.lastName || u.lastname || u.last_name || "";
        const fullName = (lname ? lname + (fname ? " " : "") : "") + fname;
        if (mounted && fullName) setOwnerDisplay(fullName);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [owner, ownerDisplay]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "amount") {
      setIleya(prev => ({ ...prev, amount: formatWithCommas(value) }));
    } else {
      setIleya(prev => ({ ...prev, [name]: value }));
    }
  };

  const saveIleya = async () => {
    setError("");
    if (!Ileya.amount || Number.isNaN(Number(String(Ileya.amount).replace(/,/g, "")))) {
      setError("Please enter a valid amount");
      return;
    }
    if (!Ileya.creditOrDebit) {
      setError("Please select transaction type");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        owner: Ileya.owner,
        date: typeof Ileya.date === "string" ? Ileya.date : toDateInput(Ileya.date),
        amount: Number(String(Ileya.amount).replace(/,/g, "")),
        creditOrDebit: String(Ileya.creditOrDebit),
        description: (Ileya.description || "").trim()
      };

      const res = await IleyaSavingsDataService.create(Ileya.owner, payload);
      const r = res?.data || {};

      setIleya({
        owner: r.owner ?? Ileya.owner,
        date: r.date ?? payload.date,
        amount: r.amount ?? payload.amount,
        creditOrDebit: r.creditOrDebit ?? payload.creditOrDebit,
        description: r.description ?? payload.description,
        createdAt: r.createdAt ?? null,
        createdBy: r.createdBy ?? null,
        updatedAt: r.updatedAt ?? null,
        updatedBy: r.updatedBy ?? null
      });

      navigate(`/dashboard/IleyaSavings/${encodeURIComponent(r.owner ?? Ileya.owner)}`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const newIleya = () => {
    setIleya({ ...initialIleyaState, owner: owner || initialIleyaState.owner });
    setSubmitted(false);
    setError("");
    if (!owner) setOwnerDisplay("");
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, margin: "24px auto", p: { xs: 2, md: 4 } }}>
      <Typography variant="h6" gutterBottom>
        {submitted ? "Transaction saved" : "Add IleyaSavings Transaction"}
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {submitted ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" onClick={newIleya}>
              Add Another
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                id="owner"
                name="owner"
                label="Owner"
                value={ownerDisplay || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={owner ? "Owner provided via route (read-only)" : "Owner (read-only)"}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="date"
                name="date"
                label="Date of Deposit"
                helperText="Date deposit occurred (system creation timestamp is recorded automatically)"
                type="date"
                value={Ileya.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="amount"
                name="amount"
                label="Amount"
                type="text"
                placeholder="e.g. 4,000,000.00"
                value={Ileya.amount}
                onChange={handleInputChange}
                onBlur={() => {
                  if (Ileya.amount) {
                    setIleya(prev => ({ ...prev, amount: formatToTwoDecimals(prev.amount) }));
                  }
                }}
                fullWidth
                InputProps={{ inputMode: "numeric" }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">Transaction Type</Typography>
                <Button
                  variant={Ileya.creditOrDebit === "credit" ? "contained" : "outlined"}
                  onClick={() => setIleya(s => ({ ...s, creditOrDebit: "credit" }))}
                >
                  Credit
                </Button>
                <Button
                  variant={Ileya.creditOrDebit === "debit" ? "contained" : "outlined"}
                 onClick={() => setIleya(s => ({ ...s, creditOrDebit: "debit" }))}
                >
                  Debit
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description (optional)"
                value={Ileya.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter a brief description of the transaction"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveIleya}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                  Submit
                </Button>
                <Button variant="outlined" onClick={newIleya} disabled={loading}>
                  Reset
                </Button>
                <Button variant="text" onClick={() => window.history.back()}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          Transaction saved successfully
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AddIleyaSavings;
